import { ChangeEventHandler, FC, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Form, Input, Row, Space, Table, Tag } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useTranslation } from 'react-i18next';
import {
  MedicalTreatmentValues,
  MEDICAL_TREATMENT_COLOR,
  MEDICAL_TREATMENT_LABEL
} from 'constants/appointments';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { useUIDispatchContext } from 'providers/UI';
import {
  fetchTreatmentForms,
  updateTreatmentForm
} from 'services/api/treatmentForm';
import { TreatmentFormListRow } from 'types/api/treatmentForm';

import useOrgEffect from 'hooks/useOrgEffect';
import { ActionMenu, PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';

const PAGE_LIMIT = 10;

const MedicalTreatment: FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const timer = useRef<NodeJS.Timeout>();
  const page = Number(searchParams.get('page') || 1);
  const { toggleModal } = useUIDispatchContext();

  const [results, setResults] = useState<TreatmentFormListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchTreatmentForms({
          page,
          limit: PAGE_LIMIT,
          search: search || undefined
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, search, refreshKey]);

  const onStatusSubmit = async (
    id: string,
    values: { note: string; status: string }
  ) => {
    try {
      await updateTreatmentForm(id, {
        status: String(values.status),
        note: values.note
      });
      toggleModal('updateStatus', false);
      setRefreshKey(k => k + 1);
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const onSearch: ChangeEventHandler<HTMLInputElement> = e => {
    searchParams.set('page', '0');
    setSearchParams(searchParams);

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      setSearch(e.target.value);
    }, 700);
  };

  return (
    <PageWrapper title="Medical Treatment" color="white">
      <Row>
        <Col sm={8}>
          <Space>
            <Form.Item>
              <Input.Search
                placeholder={t('common.search')}
                onChange={onSearch}
              />
            </Form.Item>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Name',
            dataIndex: 'first_name',
            key: 'name',
            render(_, record) {
              return `${record.first_name} ${record.last_name}`;
            }
          },
          {
            title: 'Contact data',
            dataIndex: 'email',
            key: 'email',
            render(_, record) {
              return (
                <>
                  <b>Phone:</b>{' '}
                  <a href={`tel:${record.phone}`}>{record.phone}</a>
                  <br />
                  <b>Email:</b>{' '}
                  <a href={`mailto:${record.email}`}>{record.email}</a>
                </>
              );
            }
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render(value: MedicalTreatmentValues) {
              return (
                <Tag color={MEDICAL_TREATMENT_COLOR[value]}>
                  {MEDICAL_TREATMENT_LABEL[value]}
                </Tag>
              );
            }
          },
          {
            title: 'Created at',
            dataIndex: 'created_at',
            key: 'created_at',
            render(createdAt: string) {
              return dayjs(createdAt).format(FULL_DATE_FORMAT);
            }
          },
          {
            title: '',
            dataIndex: 'id',
            key: 'actions',
            align: 'right',
            render(id: number | string, record) {
              const idStr = String(id);

              return (
                <ActionMenu
                  data={[
                    {
                      title: 'Change status',
                      permission: {
                        route: PERMISSION_ROUTES.medicalTreatment,
                        action: PERMISSION_ACTIONS.update
                      },
                      action() {
                        toggleModal('updateStatus', true, {
                          id: idStr,
                          currentStatus: `${record.status}`,
                          statusList: MEDICAL_TREATMENT_LABEL,
                          onSubmit: values => {
                            onStatusSubmit(idStr, values);
                          }
                        });
                      }
                    },
                    {
                      title: 'View',
                      action() {
                        navigate(idStr);
                      }
                    }
                  ]}
                  placement="bottomRight"
                  icon={<EllipsisOutlined />}
                />
              );
            }
          }
        ]}
        loading={loading}
        rowKey="id"
        pagination={{
          total: total || 0,
          current: page,
          onChange: p => {
            searchParams.set('page', `${p}`);
            setSearchParams(searchParams);
          },
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default MedicalTreatment;
